import React, { useCallback, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  LayoutChangeEvent,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  View,
} from "react-native"
import { ScrollView } from "react-native-gesture-handler"

import { isAndroid, isIos } from "@braintree/browser-detection"
import styled from "styled-components/native"

import icons from "@treefort/tokens/app/icons"

import Icon from "./icon"
import LinearGradient from "./linear-gradient"
import { Pill } from "./pill"
import Row from "./row"
import { useTokens } from "./tokens-provider"
import Touchable from "./touchable"

const HIT_SLOP_PX = 8
const ARROW_SCROLL_PX = 250

type PillData<T> = {
  id: string
  label: string
  groupId?: number
  value: T
}

type PillSelectionBehavior = "radiobuttons" | "checkboxes"

type Coordinate = { x: number; y: number }

const Arrow = styled(Touchable)<{
  direction: "left" | "right"
  isVisible: boolean
}>`
  position: absolute;
  ${({ direction }) => (direction === "left" ? "left: 0;" : "right: -1px;")}
  top: 0;
  z-index: 1000;
  height: 100%;
  justify-content: center;
  align-items: ${({ direction }) =>
    direction === "left" ? "flex-start" : "flex-end"};
  display: ${({ isVisible }) => (isVisible ? "flex" : "none")};
  width: ${({ theme }) => theme.minTapTarget}px;
`

const GradientBackground = styled(LinearGradient)`
  height: 100%;
  width: 100%;
  position: absolute;
  top: 0;
`

const PanScrollView = styled(ScrollView)`
  margin: 0;
  padding: ${({ theme }) => theme.spacing.medium}px 0;
`

const isNative = Platform.OS !== "web" || isIos() || isAndroid()

export function PillGroup<TValue, TSelectAllValue>({
  id,
  pills,
  selectAllValue,
  selectionBehavior = "radiobuttons",
  onSelectionChange,
}: {
  id: string
  pills: PillData<TValue>[]
  selectAllValue?: TSelectAllValue
  selectionBehavior: PillSelectionBehavior
  onSelectionChange: (selectedValues: (TValue | TSelectAllValue)[]) => void
}) {
  const { t } = useTranslation()
  const { tokens } = useTokens()
  const [selectedValues, setSelectedValues] = useState<TValue[]>([])

  const scrollViewRef = useRef<ScrollView>(null)
  const isPanning = useRef(false)
  const scrollViewActualWidth = useRef(0)
  const rowDesiredWidth = useRef(0)

  const [scrollPosition, setScrollPosition] = useState<Coordinate>({
    x: 0,
    y: 0,
  })
  const [originalScrollPosition, setOriginalScrollPosition] =
    useState<Coordinate | null>(null)

  const [originalPointerPagePosition, setOriginalPointerPagePosition] =
    useState<Coordinate | null>(null)

  const [hasMoreContentRightward, setHasMoreContentRightward] = useState(false)
  const [hasMoreContentLeftward, setHasMoreContentLeftward] = useState(false)

  const updateScrollArrowVisibility = useCallback(
    (scrollCoordinate: Coordinate) => {
      if (scrollViewActualWidth.current >= rowDesiredWidth.current) {
        setHasMoreContentLeftward(false)
        setHasMoreContentRightward(false)
        return
      }

      setHasMoreContentRightward(
        rowDesiredWidth.current - scrollViewActualWidth.current >
          scrollCoordinate.x,
      )
      setHasMoreContentLeftward(scrollCoordinate.x > 0)
    },
    [],
  )

  const updateScrollPosition = useCallback(
    (newCoord: Coordinate, animated: boolean) => {
      if (!scrollViewRef.current) {
        return
      }

      scrollViewRef.current.scrollTo({ ...newCoord, animated })
      setScrollPosition(newCoord)

      updateScrollArrowVisibility(newCoord)
    },
    [scrollViewRef, updateScrollArrowVisibility],
  )

  const updatePanPosition = useCallback(
    (currentPointerPagePosition: Coordinate) => {
      if (
        !originalScrollPosition ||
        !originalPointerPagePosition ||
        !scrollViewRef.current
      ) {
        return
      }

      const dragDelta = {
        x: currentPointerPagePosition.x - originalPointerPagePosition.x,
        y: currentPointerPagePosition.y - originalPointerPagePosition.y,
      }
      const newCoord = {
        x: originalScrollPosition.x - dragDelta.x,
        y: originalScrollPosition.y - dragDelta.y,
      }

      if (
        Math.abs(dragDelta.x) > HIT_SLOP_PX ||
        Math.abs(dragDelta.y) > HIT_SLOP_PX
      ) {
        isPanning.current = true

        updateScrollPosition(newCoord, false)
      }
    },
    [originalPointerPagePosition, originalScrollPosition, updateScrollPosition],
  )

  const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
    setScrollPosition(e.nativeEvent.contentOffset)
  }

  const displayPills = [
    selectAllValue !== undefined
      ? {
          id: `${id}-select-all`,
          label: t("All"),
          value: selectAllValue as TSelectAllValue,
        }
      : null,
    ...pills,
  ].flatMap((value) => value || [])

  function isSelectAllValue(
    value: TSelectAllValue | TValue | undefined,
  ): value is TSelectAllValue {
    return (
      selectAllValue !== undefined &&
      value !== undefined &&
      value === selectAllValue
    )
  }

  const onPillGroupLayout = useCallback(
    (e: LayoutChangeEvent) => {
      rowDesiredWidth.current = e.nativeEvent.layout.width
      updateScrollArrowVisibility(scrollPosition)
    },
    [updateScrollArrowVisibility, scrollPosition],
  )

  return (
    <View>
      {isNative ? null : (
        <Arrow
          id={`${id}-arrow-left`}
          direction="left"
          onPress={() =>
            updateScrollPosition(
              {
                x: scrollPosition.x - ARROW_SCROLL_PX,
                y: scrollPosition.y,
              },
              true,
            )
          }
          isVisible={hasMoreContentLeftward}
        >
          <GradientBackground
            fadeTowards="right"
            color={tokens.colors.background.primary}
          />
          <Icon source={icons.chevronLeft} size="small" />
        </Arrow>
      )}
      <PanScrollView
        ref={scrollViewRef}
        horizontal={true}
        showsHorizontalScrollIndicator={false}
        onScroll={handleScroll}
        onPointerDown={(e) => {
          setOriginalScrollPosition({ ...scrollPosition })
          setOriginalPointerPagePosition({
            x: e.nativeEvent.pageX,
            y: e.nativeEvent.pageY,
          })
          // This clears the panning state in case a previous pan
          // happened between pills, which would mean it didn't get
          // cleared from a pill.onPress handler
          isPanning.current = false
        }}
        onPointerUp={() => {
          setOriginalPointerPagePosition(null)
          setOriginalScrollPosition({ ...scrollPosition })
        }}
        onPointerMove={(e) => {
          updatePanPosition({
            x: e.nativeEvent.pageX,
            y: e.nativeEvent.pageY,
          })
        }}
        onLayout={(event) => {
          scrollViewActualWidth.current = event.nativeEvent.layout.width
          updateScrollArrowVisibility(scrollPosition)
        }}
      >
        <Row
          gap="small"
          onLayout={onPillGroupLayout}
          paddingLeft={isNative ? "pagePaddingHorizontal" : "none"} // Aligns the leftmost pill with content before scrolling
          paddingRight={isNative ? "pagePaddingHorizontal" : "none"} // Aligns the rightmost pill with content after scrolling all the way right
        >
          {displayPills.map((pill) => (
            <Pill
              key={pill.id}
              id={pill.id}
              selected={
                selectedValues.some((value) => value === pill.value) ||
                (selectedValues.length === 0 && pill.value === selectAllValue)
              }
              value={pill.value}
              onPress={() => {
                // Gross hack: because we have been unable to successfully intercept pointer events
                // during the capture phase, we have here an event suppression to prevent pills
                // from being selected on mouseup after a pan.
                if (isPanning.current) {
                  isPanning.current = false
                  return
                }

                if (isSelectAllValue(pill.value)) {
                  setSelectedValues([])
                  onSelectionChange([pill.value])
                  return
                }

                let newValues: TValue[]

                switch (selectionBehavior) {
                  case "checkboxes":
                    if (selectedValues.some((value) => value === pill.value)) {
                      newValues = selectedValues.filter(
                        (value) => value !== pill.value,
                      )
                    } else {
                      newValues = [...selectedValues, pill.value].flatMap(
                        (value) => value || [],
                      )
                    }
                    break
                  case "radiobuttons":
                    newValues = pill.value ? [pill.value] : []
                    break
                }

                setSelectedValues(newValues)
                onSelectionChange(newValues)
              }}
            >
              {pill.label}
            </Pill>
          ))}
        </Row>
      </PanScrollView>
      {isNative ? null : (
        <Arrow
          id={`${id}-arrow-right`}
          direction="right"
          onPress={() => {
            updateScrollPosition(
              {
                x: scrollPosition.x + ARROW_SCROLL_PX,
                y: scrollPosition.y,
              },
              true,
            )
          }}
          isVisible={hasMoreContentRightward}
        >
          <GradientBackground
            fadeTowards="left"
            color={tokens.colors.background.primary}
          />
          <Icon touchable={true} source={icons.chevronRight} size="small" />
        </Arrow>
      )}
    </View>
  )
}
